import { useCallback, useEffect, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWatch, Controller } from 'react-hook-form';
import { enqueueSnackbar, OptionsObject } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';

import {
  reportFilterCountryCodeSelector,
  executeSynchroRequested,
  synchroAuthorizedCountriesSelector,
  synchroEnabledCountriesSelector,
} from '../../state';
import { StoreSelect } from '../selects';
import { AppPermissions, CreateSynchroForm, Stores } from '../../types';
import { PermissionsProvider } from '../Permissions';
import {
  CountrySelectFormInput,
  DatePickerFormInput,
  FlexSalesForm,
  TextFieldFormInput,
  useFlexSalesForm,
  FlexSalesFormActions,
  ResetWhenFieldChanges,
} from '../FlexSalesForm';

import { getSchema } from './schema';

const initialValues: CreateSynchroForm = {
  country: '',
  stores: [],
  justification: '',
  businessDateFrom: null,
  businessDateTo: null,
};

const messageOptions: OptionsObject = {
  variant: 'success',
  autoHideDuration: 6000,
  anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
};

const ConnectedStoreMultiSelect = () => {
  const { control } = useFlexSalesForm();
  const country = useWatch({ control, name: 'country' });
  const { t } = useTranslation();

  return (
    <Controller
      name="stores"
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <StoreSelect
          label={t('table.columns.store') || 'Stores'}
          limitTags={3}
          selectAll
          simple={false}
          countryCode={country}
          value={value || []}
          onChange={onChange}
          isOptionEqualToValue={(option: Stores, val: Stores) =>
            option?.storeAcronym === val?.storeAcronym
          }
          error={!!error}
          helperText={error ? error.message : null}
          fullWidth
          required
        />
      )}
    />
  );
};

const InnerForm = ({
  onCancel,
  isCountryLocked,
  allowedCountryCodes,
}: {
  onCancel: () => void;
  isCountryLocked: boolean;
  allowedCountryCodes: string[];
}) => {
  const { t } = useTranslation();
  const { control, trigger } = useFlexSalesForm();
  const businessDateFrom = useWatch({ control, name: 'businessDateFrom' });

  useEffect(() => {
    if (businessDateFrom) trigger('businessDateTo');
  }, [businessDateFrom, trigger]);

  return (
    <>
      <ResetWhenFieldChanges field="country" fieldToReset="stores" />
      <Grid container flexDirection="column" rowSpacing={1}>
        <Grid item xs>
          <CountrySelectFormInput
            name="country"
            fullWidth
            required
            disabled={isCountryLocked}
            allowedCountryCodes={allowedCountryCodes}
          />
        </Grid>
        <Grid item xs>
          <ConnectedStoreMultiSelect />
        </Grid>
        <Grid item xs>
          <DatePickerFormInput
            name="businessDateFrom"
            label={t('synchro4me.columns.fromDateAccounting') || 'Date From'}
            required
          />
        </Grid>
        <Grid item xs>
          <DatePickerFormInput
            name="businessDateTo"
            label={t('synchro4me.columns.toDateAccounting') || 'Date To'}
            required
          />
        </Grid>
        <Grid item xs>
          <TextFieldFormInput
            name="justification"
            variant="standard"
            label={t('synchro4me.columns.justification') || 'Justification'}
            required
            fullWidth
          />
        </Grid>
      </Grid>
      <FlexSalesFormActions
        onCancel={onCancel}
        cancelLabel={t('reprocess.labels.cancel') || 'Cancel'}
        submitLabel={t('navigation.buttons.synchro') || 'Send'}
      />
    </>
  );
};

const SynchroExecutionForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const countryCodeFromSearch = useSelector(reportFilterCountryCodeSelector);
  const authorizedCountries = useSelector(synchroAuthorizedCountriesSelector);
  const enabledCountries = useSelector(synchroEnabledCountriesSelector);

  const availableCountries = useMemo(
    () => authorizedCountries.filter(c => enabledCountries.includes(c.countryCode)),
    [authorizedCountries, enabledCountries]
  );

  const isCountryLocked = availableCountries.length === 1;

  const currentInitialValues = useMemo(
    () => ({
      ...initialValues,
      country: countryCodeFromSearch || availableCountries[0]?.countryCode || '',
    }),
    [countryCodeFromSearch, availableCountries]
  );

  const handleCancel = useCallback(() => {
    navigate('/synchro-for-me');
  }, [navigate]);

  const onSubmit = async (data: CreateSynchroForm) => {
    const payload = {
      countryAcronym: data.country,
      stores: data.stores.map((s: Stores) => s.storeAcronym).join(','),
      businessDateFrom: data.businessDateFrom ? format(data.businessDateFrom, 'yyyy-MM-dd') : '',
      businessDateTo: data.businessDateTo ? format(data.businessDateTo, 'yyyy-MM-dd') : '',
      justification: data.justification,
    };

    dispatch(executeSynchroRequested(payload));
    navigate('/synchro-for-me');
    enqueueSnackbar(t('synchro4me.messages.executionSuccess'), messageOptions);
  };

  return (
    <PermissionsProvider resourcePermissions={[AppPermissions.SYNCHRO_4_ME]}>
      <FlexSalesForm
        onValidSubmit={onSubmit}
        resolver={useMemo(() => yupResolver(getSchema()), [])}
        defaultValues={currentInitialValues}
      >
        <InnerForm
          onCancel={handleCancel}
          isCountryLocked={isCountryLocked}
          allowedCountryCodes={enabledCountries}
        />
      </FlexSalesForm>
    </PermissionsProvider>
  );
};

export default SynchroExecutionForm;
